import { sendChat, searchVideos } from './api';
import { CARD_THEME_MAP } from './config';
import {
  clearElement,
  createErrorBubble,
  createLoadingIndicator,
  createMessageBubble,
  createStatusMessage,
  createVideoResults,
} from './dom';
import { RealtimeAudioController, type RealtimeEventPayload } from './audio/realtime';
import type { ChatStore } from './storage';
import type { VideoModalController } from './video/modal';
import type { CardSummary, HistoryMessage, LocaleCode } from '../src/shared/contracts';

interface ChatViewOptions {
  card: CardSummary;
  lang: LocaleCode;
  store: ChatStore;
  videoModal: VideoModalController;
  onClose: () => void;
}

export class ChatView {
  private readonly root: HTMLElement;
  private readonly chatElement: HTMLElement;
  private readonly chatHistory: HTMLElement;
  private readonly chatForm: HTMLFormElement;
  private readonly messageInput: HTMLInputElement;
  private readonly audioToggle: HTMLButtonElement | null;
  private audioController: RealtimeAudioController | null = null;
  private pendingAssistantBubble: HTMLDivElement | null = null;
  private currentAssistantItemId: string | null = null;
  private ignoreNextRealtimeUserMessage = false;

  constructor(private readonly options: ChatViewOptions) {
    const template = document.getElementById('chat-container-template') as HTMLTemplateElement | null;
    if (!template) {
      throw new Error('Missing chat container template.');
    }

    const fragment = template.content.cloneNode(true) as DocumentFragment;
    const root = fragment.firstElementChild as HTMLElement | null;
    if (!root) {
      throw new Error('Chat template is empty.');
    }

    this.root = root;
    this.chatElement = this.root.querySelector('.chat-container') as HTMLElement;
    this.chatHistory = this.root.querySelector('.chat-history') as HTMLElement;
    this.chatForm = this.root.querySelector('.chat-form') as HTMLFormElement;
    this.messageInput = this.root.querySelector('.message-input') as HTMLInputElement;
    this.audioToggle = this.root.querySelector('.audio-toggle-button');

    this.applyTheme();
    this.bindEvents();
    this.renderHistory();
  }

  mount(): void {
    document.body.append(this.root);
    this.chatElement.style.display = 'flex';
    this.messageInput.focus();
  }

  destroy(): void {
    this.disconnectAudio();
    this.root.remove();
  }

  private bindEvents(): void {
    this.root.querySelector('.chat-title')!.textContent = this.options.card.name;
    this.root.querySelector('.back-button')!.addEventListener('click', () => this.options.onClose());
    this.root.querySelector('.clear-button')!.addEventListener('click', () => {
      this.options.store.clearHistory(this.options.lang, this.options.card.id);
      this.renderHistory();
    });

    this.chatForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const message = this.messageInput.value.trim();
      if (!message) {
        return;
      }

      this.messageInput.value = '';
      void this.handleSubmit(message);
    });

    if (this.options.card.isAudio && this.audioToggle) {
      this.audioToggle.addEventListener('click', () => void this.toggleAudio());
    } else if (this.audioToggle?.parentElement) {
      this.audioToggle.parentElement.style.display = 'none';
    }
  }

  private applyTheme(): void {
    const theme = CARD_THEME_MAP[this.options.card.color];
    const header = this.root.querySelector('.chat-header') as HTMLElement;
    header.className = `chat-header p-4 flex justify-between items-center shadow-md ${theme.header}`;
    this.chatElement.classList.add(theme.surface);
  }

  private renderHistory(): void {
    clearElement(this.chatHistory);
    const history = this.ensureHistory();

    for (const message of history) {
      if (message.kind === 'video_results') {
        this.chatHistory.append(createVideoResults(message.videos, (videoId) => void this.options.videoModal.open(videoId)));
        continue;
      }

      this.chatHistory.append(createMessageBubble(message));
    }

    this.scrollToBottom();
  }

  private ensureHistory(): HistoryMessage[] {
    const history = this.options.store.getHistory(this.options.lang, this.options.card.id);
    if (history.length > 0) {
      return history;
    }

    const nextHistory: HistoryMessage[] = [
      {
        kind: 'text',
        sender: 'assistant',
        text: this.options.card.welcomeMessage,
      },
    ];
    this.options.store.setHistory(this.options.lang, this.options.card.id, nextHistory);
    return nextHistory;
  }

  private setHistory(history: HistoryMessage[]): void {
    this.options.store.setHistory(this.options.lang, this.options.card.id, history);
  }

  private appendHistory(message: HistoryMessage): void {
    const history = this.ensureHistory();
    history.push(message);
    this.setHistory(history);
  }

  private async handleSubmit(messageText: string): Promise<void> {
    const historyBeforeMessage = [...this.ensureHistory()];
    this.appendHistory({ kind: 'text', sender: 'user', text: messageText });
    this.renderHistory();

    if (this.audioController?.connected) {
      this.ignoreNextRealtimeUserMessage = true;
      try {
        this.audioController.sendTextMessage(messageText);
        return;
      } catch (error) {
        this.chatHistory.append(createErrorBubble(error instanceof Error ? error.message : 'Audio mode failed.'));
        this.disconnectAudio();
      }
    }

    const loadingIndicator = createLoadingIndicator();
    this.chatHistory.append(loadingIndicator);
    this.scrollToBottom();

    try {
      const response = await sendChat({
        cardId: this.options.card.id,
        history: historyBeforeMessage,
        lang: this.options.lang,
        message: messageText,
      });

      loadingIndicator.remove();

      if (response.kind === 'message') {
        this.appendHistory({ kind: 'text', sender: 'assistant', text: response.text });
      } else {
        this.appendHistory({ kind: 'text', sender: 'assistant', text: response.confirmationText });
        await this.performVideoSearch(response.searchTerms);
      }

      this.renderHistory();
    } catch (error) {
      loadingIndicator.remove();
      this.chatHistory.append(createErrorBubble(error instanceof Error ? error.message : 'Unable to reach the assistant.'));
      this.scrollToBottom();
    }
  }

  private async performVideoSearch(searchTerms: string): Promise<void> {
    const loadingIndicator = createLoadingIndicator();
    this.chatHistory.append(loadingIndicator);
    this.scrollToBottom();

    try {
      const videos = await searchVideos(searchTerms);
      loadingIndicator.remove();

      if (videos.length === 0) {
        this.appendHistory({ kind: 'text', sender: 'assistant', text: 'No videos found for your query.' });
      } else {
        this.appendHistory({ kind: 'video_results', sender: 'assistant', videos });
      }
    } catch (error) {
      loadingIndicator.remove();
      this.chatHistory.append(createErrorBubble(error instanceof Error ? error.message : 'Video search failed.'));
      this.scrollToBottom();
    }
  }

  private async toggleAudio(): Promise<void> {
    if (this.audioController?.connected) {
      this.disconnectAudio();
      return;
    }

    if (!this.audioToggle) {
      return;
    }

    this.audioToggle.classList.add('connecting');
    this.chatForm.classList.add('audio-mode');
    const controller = new RealtimeAudioController();

    try {
      await controller.connect(this.options.card.id, this.options.lang, {
        onEvent: (event) => this.handleRealtimeEvent(event),
        onInactivity: () => {
          this.chatHistory.append(createStatusMessage('Audio mode deactivated due to inactivity.'));
          this.scrollToBottom();
          this.disconnectAudio();
        },
      });

      this.audioController = controller;
      this.audioToggle.classList.remove('connecting');
      this.audioToggle.classList.add('active');
    } catch (error) {
      this.audioToggle.classList.remove('connecting');
      this.chatForm.classList.remove('audio-mode');
      this.chatHistory.append(createErrorBubble(error instanceof Error ? error.message : 'Unable to start audio mode.'));
      this.scrollToBottom();
      controller.disconnect();
    }
  }

  private disconnectAudio(): void {
    this.audioController?.disconnect();
    this.audioController = null;
    this.pendingAssistantBubble = null;
    this.currentAssistantItemId = null;
    this.ignoreNextRealtimeUserMessage = false;
    this.audioToggle?.classList.remove('active', 'connecting');
    this.chatForm.classList.remove('audio-mode');
  }

  private handleRealtimeEvent(event: RealtimeEventPayload): void {
    switch (event.type) {
      case 'conversation.item.created':
        this.handleConversationItemCreated(event);
        break;
      case 'response.audio_transcript.delta':
        this.handleTranscriptDelta(event);
        break;
      case 'response.audio_transcript.done':
        this.handleTranscriptDone(event);
        break;
      case 'response.done':
        this.finalizePendingRealtimeTranscript();
        break;
      case 'error':
        this.chatHistory.append(createErrorBubble(String((event.error as { message?: string } | undefined)?.message ?? 'Audio error.')));
        this.scrollToBottom();
        break;
      default:
        break;
    }
  }

  private handleConversationItemCreated(event: RealtimeEventPayload): void {
    const item = event.item as { id?: string; role?: string } | undefined;
    if (!item) {
      return;
    }

    if (item.role === 'assistant' && item.id) {
      this.currentAssistantItemId = item.id;
      return;
    }

    if (item.role === 'user') {
      if (this.ignoreNextRealtimeUserMessage) {
        this.ignoreNextRealtimeUserMessage = false;
        return;
      }

      this.appendHistory({ kind: 'text', sender: 'user', text: '[audio input]' });
      this.renderHistory();
    }
  }

  private handleTranscriptDelta(event: RealtimeEventPayload): void {
    if (event.item_id !== this.currentAssistantItemId) {
      return;
    }

    if (!this.pendingAssistantBubble) {
      this.pendingAssistantBubble = createMessageBubble({ kind: 'text', sender: 'assistant', text: '' });
      this.pendingAssistantBubble.classList.add('realtime-message');
      this.chatHistory.append(this.pendingAssistantBubble);
    }

    const delta = String(event.delta ?? '');
    this.pendingAssistantBubble.textContent = `${this.pendingAssistantBubble.textContent ?? ''}${delta}`;
    this.scrollToBottom();
  }

  private handleTranscriptDone(event: RealtimeEventPayload): void {
    if (event.item_id !== this.currentAssistantItemId) {
      return;
    }

    const transcript = String(event.transcript ?? '').trim();
    if (!transcript) {
      return;
    }

    this.finalizePendingRealtimeTranscript(transcript);
  }

  private finalizePendingRealtimeTranscript(finalText?: string): void {
    const text = finalText ?? this.pendingAssistantBubble?.textContent?.trim();
    if (!text) {
      this.pendingAssistantBubble?.remove();
      this.pendingAssistantBubble = null;
      return;
    }

    this.pendingAssistantBubble?.remove();
    this.pendingAssistantBubble = null;
    this.appendHistory({ kind: 'text', sender: 'assistant', text });
    this.renderHistory();
  }

  private scrollToBottom(): void {
    this.chatHistory.scrollTop = this.chatHistory.scrollHeight;
  }
}
